import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Info, X } from 'lucide-react';
import { calculateOwnerDiagnosticScores } from '../../../lib/ownerDiagnosticScoring';
import Button from '../../../../../ui/canon/Button';
import { pctSymbol, formatCurrency } from '../../../utils/formatters';

const GAPS_QUERY = gql`
  query YourGapsCompactData {
    getCurrentUser {
      id
      features
      company { id features feature_types }
      diag_owner_answers
      diag_business_answers
      retirement_timeline_bucket
      readiness_current_investable_assets
      value_gap_current_value
      value_gap_gap_value
      valuation_last_accessed_date
      triggering_events { event_type completed_at payload }
      action_plan { items { id status cepaCategory } }
    }
  }
`;

const GET_VAL = gql`
  query GetValForGaps($input: Input) {
    successionValuationData(input: $input) { valueOfPractice }
  }
`;

const GET_MULTI_LINE_VAL = gql`
  query GetMultiLineValuationForGaps($input: MultiLineValuationInput!) {
    multiLineValuation(input: $input) {
      combined_summary
      combined_summary_adjusted
    }
  }
`;

const GET_MULTI_LINE_SCENARIOS_FOR_GAPS = gql`
  query GetMultiLineScenariosForGaps {
    multiLineScenarios {
      id
      results
      updatedAt
    }
  }
`;

const UPDATE_VALUE_GAP = gql`
  mutation UpdateUserValueGapFromValuation(
    $value_gap_current_value: Float,
    $value_gap_gap_value: Float,
    $value_gap_last_updated_at: ISO8601DateTime
  ) {
    updateCurrentUser(
      value_gap_current_value: $value_gap_current_value,
      value_gap_gap_value: $value_gap_gap_value,
      value_gap_last_updated_at: $value_gap_last_updated_at
    )
  }
`;

const currency = (n) => formatCurrency(Number(n || 0));

// Helper for JSON blobs that may have symbolized, snake_case, or camelCase keys
const pick = (obj, keys) => {
  if (!obj || typeof obj !== 'object') return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined) return obj[k];
  }
  return undefined;
};

const parseBucketToRange = (bucket) => {
  if (typeof bucket === 'string') {
    const m = bucket.match(/(\d+)\s*[-–]\s*(\d+)/);
    if (m) return { min: Number(m[1]), max: Number(m[2]) };
  }
  return { min: 4, max: 6 };
};

const IconCalendar = ({ size = 20, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
    <rect x="3" y="5" width="18" height="16" rx="2" ry="2" stroke="currentColor" />
    <path d="M16 3v4M8 3v4M3 9h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const IconCheckCircle = ({ size = 20, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
    <circle cx="10" cy="10" r="8" stroke="currentColor" fill="none" />
    <path d="M6.5 10.5l2.5 2.5 4.5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconTrendingUpIcon = ({ size = 20, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
    <path d="M3 17l6-6 4 4 7-7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14 8h7v7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

// Helper for score display
const getScoreColor = (score) => {
  if (score >= 80) return 'text-succession-success';
  if (score >= 60) return 'text-succession-warning';
  return 'text-succession-error';
};

// Modal for Readiness Scores (Personal Readiness & Buyer Attractiveness)
const ReadinessScoreModal = ({ isOpen, onClose, type, score }) => {
  const intl = useIntl();

  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const READINESS_DEFINITIONS = {
    personal: {
      name: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_personal_title' }),
      description: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_personal_description' }),
      areas: [
        { label: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_area_personal_financial_plan' }), detail: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_area_personal_financial_plan_detail' }) },
        { label: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_area_net_worth' }), detail: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_area_net_worth_detail' }) },
        { label: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_area_post_exit_goals' }), detail: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_area_post_exit_goals_detail' }) },
        { label: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_area_family_alignment' }), detail: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_area_family_alignment_detail' }) },
        { label: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_area_emotional_readiness' }), detail: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_area_emotional_readiness_detail' }) },
        { label: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_area_estate_planning' }), detail: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_area_estate_planning_detail' }) },
      ],
      getStatus: (s) => {
        if (s >= 80) return { label: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_status_ready' }), color: 'text-succession-success', bg: 'bg-succession-success/10' };
        if (s >= 60) return { label: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_status_moderately_ready' }), color: 'text-succession-warning', bg: 'bg-succession-warning/10' };
        return { label: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_status_not_ready' }), color: 'text-succession-error', bg: 'bg-succession-error/10' };
      },
      improvementTip: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_personal_improvement_tip' }),
    },
    buyer: {
      name: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_buyer_title' }),
      description: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_buyer_description' }),
      areas: [
        { label: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_area_revenue_growth' }), detail: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_area_revenue_growth_detail' }) },
        { label: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_area_recurring_revenue' }), detail: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_area_recurring_revenue_detail' }) },
        { label: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_area_owner_independence' }), detail: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_area_owner_independence_detail' }) },
        { label: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_area_client_diversity' }), detail: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_area_client_diversity_detail' }) },
        { label: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_area_market_position' }), detail: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_area_market_position_detail' }) },
        { label: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_area_systems_processes' }), detail: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_area_systems_processes_detail' }) },
      ],
      getStatus: (s) => {
        if (s >= 80) return { label: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_status_attractive' }), color: 'text-succession-success', bg: 'bg-succession-success/10' };
        if (s >= 60) return { label: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_status_moderately_attractive' }), color: 'text-succession-warning', bg: 'bg-succession-warning/10' };
        return { label: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_status_needs_work' }), color: 'text-succession-error', bg: 'bg-succession-error/10' };
      },
      improvementTip: intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_buyer_improvement_tip' }),
    },
  };

  const content = READINESS_DEFINITIONS[type];
  const status = content.getStatus(score);

  return (
    <div className="fixed inset-0 z-[9999]" role="dialog" aria-modal="true" style={{ zIndex: 9999 }}>
      {/* Dark backdrop */}
      <div
        className="fixed inset-0"
        onClick={onClose}
        aria-hidden="true"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.65)',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      />

      {/* Modal content */}
      <div className="fixed inset-0 flex items-center justify-center p-4" style={{ pointerEvents: 'none', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
        <div
          className="relative rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
          style={{
            backgroundColor: '#ffffff',
            pointerEvents: 'auto',
            opacity: 1,
            zIndex: 10000,
          }}
        >
          <div className="sticky top-0 border-b border-succession-border px-6 py-4 flex items-center justify-between" style={{ backgroundColor: '#ffffff', zIndex: 10 }}>
            <h2 className="text-xl font-bold m-0 text-succession-text-primary">{content.name}</h2>
            <button onClick={onClose} className="text-succession-text-secondary hover:text-succession-text-primary transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="px-6 py-6" style={{ backgroundColor: '#ffffff' }}>
            {/* Score Display */}
            <div className="flex items-center gap-4 mb-6">
              <div className="text-5xl font-bold text-succession-text-primary">{score}%</div>
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${status.bg} ${status.color}`}>
                {status.label}
              </span>
            </div>

            {/* Description */}
            <p className="text-succession-text-secondary mb-6">{content.description}</p>

            {/* Areas Measured */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-succession-text-primary">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_what_we_measure' })}</h3>
              <ul className="space-y-3">
                {content.areas.map((area, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-succession-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-succession-primary">{idx + 1}</span>
                    </div>
                    <div>
                      <span className="font-medium text-succession-text-primary">{area.label}</span>
                      <p className="text-sm text-succession-text-secondary">{area.detail}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Improvement Tips */}
            <div className="mt-6 p-4 rounded-lg bg-succession-bg-secondary border border-succession-border">
              <h4 className="font-semibold text-succession-text-primary mb-2">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_how_to_improve' })}</h4>
              <p className="text-sm text-succession-text-secondary">{content.improvementTip}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Modal for Wealth Gap
const WealthGapModal = ({ isOpen, onClose, data, years }) => {
  const intl = useIntl();

  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const { current, goal, gap, hasSurplus, absGap } = data;

  const stepCircle = (label, bg) => (
    <div className="flex items-center justify-center w-8 h-8 rounded-full text-white text-sm font-semibold" style={{ backgroundColor: bg }}>{label}</div>
  );

  return (
    <div className="fixed inset-0 z-[9999]" role="dialog" aria-modal="true" style={{ zIndex: 9999 }}>
      {/* Dark backdrop - separate layer */}
      <div
        className="fixed inset-0"
        onClick={onClose}
        aria-hidden="true"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.65)',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      />

      {/* Modal content - separate layer with full opacity */}
      <div className="fixed inset-0 flex items-center justify-center p-4" style={{ pointerEvents: 'none', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
        <div
          className="relative rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
          style={{
            backgroundColor: '#ffffff !important',
            pointerEvents: 'auto',
            opacity: 1,
            zIndex: 10000,
          }}
        >
          <div className="sticky top-0 border-b border-succession-border px-6 py-4 flex items-center justify-between" style={{ backgroundColor: '#ffffff', zIndex: 10 }}>
            <h2 className="text-xl font-bold m-0 text-succession-text-primary">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_wealth_title' })}</h2>
            <button onClick={onClose} className="text-succession-text-secondary hover:text-succession-text-primary transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="px-6 py-6" style={{ backgroundColor: '#ffffff' }}>
          <h4 className="text-sm text-succession-text-secondary mb-6">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_wealth_subtitle' })}</h4>

          {/* Timeline */}
          <div className="flex items-center gap-3 p-4 rounded-lg bg-succession-bg-secondary border border-succession-border">
            <div className="flex items-center justify-center w-10 h-10 rounded-full" style={{ background: 'var(--succession-primary-10, rgba(79,70,229,0.10))' }}>
              <IconCalendar className="text-succession-primary" />
            </div>
            <div>
              <p className="text-sm text-succession-text-secondary">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_retirement_timeline' })}</p>
              <p className="text-lg font-semibold">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_years_range' }, { min: years.min, max: years.max })}</p>
            </div>
          </div>

          {/* Story steps */}
          <div className="space-y-4 mt-6">
            <div className="flex items-start gap-4">
              <div className="flex flex-col items-center">
                {stepCircle('1', 'var(--succession-success, #10b981)')}
                <div className="w-0.5 h-12 bg-succession-border mt-2" />
              </div>
              <div className="flex-1 pt-1">
                <p className="text-sm text-succession-text-secondary mb-1">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_current_assets' })}</p>
                <p className="text-2xl font-bold">{currency(current)}</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex flex-col items-center">
                {stepCircle('2', 'var(--succession-primary, #4f46e5)')}
                <div className="w-0.5 h-12 bg-succession-border mt-2" />
              </div>
              <div className="flex-1 pt-1">
                <p className="text-sm text-succession-text-secondary mb-1">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_wealth_goal' })}</p>
                <p className="text-2xl font-bold">{currency(goal)}</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold ${hasSurplus ? 'text-white' : 'text-succession-error-foreground'}`} style={{ background: hasSurplus ? 'var(--succession-success, #10b981)' : 'var(--succession-error, #ef4444)' }}>
                {hasSurplus ? <IconCheckCircle className="text-white" /> : '!'}
              </div>
              <div className="flex-1 pt-1">
                <p className="text-sm text-succession-text-secondary mb-1">{hasSurplus ? intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_wealth_surplus' }) : intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_wealth_gap' })}</p>
                <p className={`text-2xl font-bold ${hasSurplus ? 'text-succession-success' : 'text-succession-error'}`}>{currency(absGap)}</p>
                <p className="text-sm text-succession-text-secondary mt-1">
                  {hasSurplus
                    ? intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_wealth_ahead' }, { amount: currency(absGap) })
                    : intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_wealth_need_more' }, { amount: currency(absGap) })}
                </p>
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Modal for Value Gap
const ValueGapModal = ({ isOpen, onClose, data, years }) => {
  const intl = useIntl();

  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const { exitValueNeeded, currentValue, hasSurplus, absGap, pctToGoal, yearsToClose, canCloseInTime, valuationLabel } = data;
  const DEFAULT_GROWTH = 0.06;

  return (
    <div className="fixed inset-0 z-[9999]" role="dialog" aria-modal="true" style={{ zIndex: 9999 }}>
      {/* Dark backdrop - separate layer */}
      <div
        className="fixed inset-0"
        onClick={onClose}
        aria-hidden="true"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.65)',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      />

      {/* Modal content - separate layer with full opacity */}
      <div className="fixed inset-0 flex items-center justify-center p-4" style={{ pointerEvents: 'none', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
        <div
          className="relative rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
          style={{
            backgroundColor: '#ffffff !important',
            pointerEvents: 'auto',
            opacity: 1,
            zIndex: 10000,
          }}
        >
          <div className="sticky top-0 border-b border-succession-border px-6 py-4 flex items-center justify-between" style={{ backgroundColor: '#ffffff', zIndex: 10 }}>
            <h2 className="text-xl font-bold m-0 text-succession-text-primary">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_value_title' })}</h2>
            <button onClick={onClose} className="text-succession-text-secondary hover:text-succession-text-primary transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="px-6 py-6" style={{ backgroundColor: '#ffffff' }}>
          <h4 className="text-sm text-succession-text-secondary mb-6">
            {hasSurplus
              ? intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_value_meets_goal' })
              : intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_value_needs_growth' })}
          </h4>

          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="flex flex-col items-center">
                <div className="flex items-center justify-center w-8 h-8 rounded-full text-white text-sm font-semibold" style={{ background: 'var(--succession-primary, #4f46e5)' }}>1</div>
                <div className="w-0.5 h-12 bg-succession-border mt-2" />
              </div>
              <div className="flex-1 pt-1">
                <p className="text-sm text-succession-text-secondary mb-1">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_exit_value_needed' })}</p>
                <p className="text-2xl font-bold">{currency(exitValueNeeded)}</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex flex-col items-center">
                <div className="flex items-center justify-center w-8 h-8 rounded-full text-white text-sm font-semibold" style={{ background: 'var(--succession-success, #10b981)' }}>2</div>
                <div className="w-0.5 h-12 bg-succession-border mt-2" />
              </div>
              <div className="flex-1 pt-1">
                <p className="text-sm text-succession-text-secondary mb-1">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_current_business_value' }, { label: valuationLabel || 'DCF' })}</p>
                <p className="text-2xl font-bold">{currency(currentValue)}</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold ${hasSurplus ? 'text-white' : 'text-succession-error-foreground'}`} style={{ background: hasSurplus ? 'var(--succession-success, #10b981)' : 'var(--succession-error, #ef4444)' }}>
                {hasSurplus ? <IconCheckCircle className="text-white" /> : '!'}
              </div>
              <div className="flex-1 pt-1">
                <p className="text-sm text-succession-text-secondary mb-1">{hasSurplus ? intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_value_surplus' }) : intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_value_shortfall' })}</p>
                <p className={`text-2xl font-bold ${hasSurplus ? 'text-succession-success' : 'text-succession-error'}`}>{currency(absGap)}</p>
                <p className="text-sm text-succession-text-secondary mt-1">
                  {hasSurplus
                    ? intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_value_exceeds' }, { amount: currency(absGap) })
                    : intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_value_grow' }, { amount: currency(absGap) })}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-2 mt-6">
            <div className="flex justify-between text-sm">
              <span className="text-succession-text-secondary">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_progress_to_exit' })}</span>
              <span className="font-semibold">{Math.round(pctToGoal)}%</span>
            </div>
            <div className="h-3 rounded-full bg-succession-bg-secondary overflow-hidden">
              <div className="h-full" style={{ width: `${pctToGoal}%`, background: 'var(--succession-success, #10b981)', transition: 'width 0.4s ease' }} />
            </div>
          </div>

          {!canCloseInTime && (
            <div className="mt-4 border border-succession-error rounded-md p-3" style={{ background: 'color-mix(in srgb, var(--succession-error, #ef4444) 10%, transparent)' }}>
              <div className="flex items-start gap-2 text-succession-error">
                <span className="mt-0.5" aria-hidden>!</span>
                <div className="text-sm">
                  <span className="font-semibold">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_gap_unlikely' }, { min: years.min, max: years.max })}</span>{' '}
                  {intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_growth_years' }, { rate: (DEFAULT_GROWTH * 100).toFixed(0), years: yearsToClose ?? '10+' })}
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 p-4 mt-4 rounded-lg bg-succession-bg-secondary border border-succession-border">
            <div className="flex items-center justify-center w-10 h-10 rounded-full" style={{ background: 'var(--succession-primary-10, rgba(79,70,229,0.10))' }}>
              <IconTrendingUpIcon className="text-succession-primary" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-succession-text-secondary">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_current_growth' })}</p>
              <p className="text-lg font-semibold">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.modal_growth_annually' }, { rate: (DEFAULT_GROWTH * 100).toFixed(0) })}</p>
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const YourGapsCompact = ({ className = '' }) => {
  const intl = useIntl();
  const [personalModal, setPersonalModal] = useState(false);
  const [buyerModal, setBuyerModal] = useState(false);
  const [wealthModal, setWealthModal] = useState(false);
  const [valueModal, setValueModal] = useState(false);
  const [persistRetryTick, setPersistRetryTick] = useState(0);

  const { data, loading, error } = useQuery(GAPS_QUERY, { fetchPolicy: 'cache-first' });
  const [updateValueGap] = useMutation(UPDATE_VALUE_GAP);
  const lastPersistKeyRef = useRef(null);
  const persistRetryCountRef = useRef({});
  const persistRetryTimeoutRef = useRef(null);

  const u = data?.getCurrentUser || {};
  const hasAnyCompleted = Array.isArray(u?.triggering_events) && u.triggering_events.some((e) => e && e.completed_at);
  const hasAnswers = (Array.isArray(u?.diag_owner_answers) && u.diag_owner_answers.some(a => a != null && a > 0)) ||
                      (Array.isArray(u?.diag_business_answers) && u.diag_business_answers.some(a => a != null && a > 0));

  // Calculate readiness scores
  const { ownerReadinessScore, buyerAttractivenessScore } = calculateOwnerDiagnosticScores(
    u?.diag_owner_answers || [],
    u?.diag_business_answers || []
  );
  const hasReadinessData = ownerReadinessScore > 0 || buyerAttractivenessScore > 0;

  // Wealth Gap data
  const bucket = u?.retirement_timeline_bucket;
  const years = parseBucketToRange(bucket);
  const wgPayload = u?.triggering_events?.find(e => e.event_type === 'wealth_gap')?.payload || {};
  const currentFromUser = Number(u?.readiness_current_investable_assets || 0);
  const currentFromEvent = Number(wgPayload.current_assets || 0);
  const wealthCurrent = Math.max(0, Number(currentFromUser || currentFromEvent || 0));
  const wealthGoal = Math.max(0, Number(wgPayload.goal_lump_sum || 0));
  const wealthGap = Number(wealthGoal) - Number(wealthCurrent);
  const wealthHasSurplus = wealthGap < 0;
  const wealthAbsGap = Math.abs(wealthGap);
  const hasWealthData = (wealthCurrent > 0) || (wealthGoal > 0);

  // Value Gap data
  const exitValueNeeded = Number(wgPayload.goal_lump_sum || 0);
  const hasWealthGapEvent = exitValueNeeded > 0;
  const lastAccess = u?.valuation_last_accessed_date;
  const userFeatures = Array.isArray(u?.features) ? u.features : [];
  const companyFeatures = Array.isArray(u?.company?.features) ? u.company.features : [];
  const companyFeatureTypes = Array.isArray(u?.company?.feature_types) ? u.company.feature_types : [];
  const multiLineEnabled =
    userFeatures.includes('multi_line_valuation') ||
    companyFeatures.includes('multi_line_valuation') ||
    companyFeatureTypes.includes('multi_line_valuation');

  const shouldFetchVal = !loading && !!lastAccess && hasWealthGapEvent;
  const skipLegacyVal = !shouldFetchVal || multiLineEnabled;
  const skipMultiLineScenarios = !shouldFetchVal || !multiLineEnabled;

  // Always declare hooks consistently; skip the one we don't need.
  const { data: legacyValData } = useQuery(GET_VAL, {
    variables: { input: null },
    skip: skipLegacyVal,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first'
  });

  // Prefer the latest saved multi-line scenario value if available (matches user's actual multi-line valuation).
  const { data: multiLineScenariosData } = useQuery(GET_MULTI_LINE_SCENARIOS_FOR_GAPS, {
    skip: skipMultiLineScenarios,
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  });

  const latestScenario = multiLineScenariosData?.multiLineScenarios?.[0];
  const scenarioResults = latestScenario?.results || {};
  const scenarioCombinedAdjusted = pick(scenarioResults, ['combined_summary_adjusted', 'combinedSummaryAdjusted']) || {};
  const scenarioCombined = pick(scenarioResults, ['combined_summary', 'combinedSummary']) || {};
  const scenarioMidRaw = pick(scenarioCombinedAdjusted, ['mid']) ?? pick(scenarioCombined, ['mid']);
  const scenarioMid = Number(scenarioMidRaw || 0);
  const hasScenarioMid = Number.isFinite(scenarioMid) && scenarioMid > 0;

  // Fallback: compute multi-line valuation with default lines (does not include per-line inputs/expenses).
  const skipMultiLineVal = !shouldFetchVal || !multiLineEnabled || hasScenarioMid;
  const { data: multiLineValData } = useQuery(GET_MULTI_LINE_VAL, {
    variables: { input: { lines: [{ name: 'wealth' }, { name: 'life' }, { name: 'pc' }, { name: 'group' }] } },
    skip: skipMultiLineVal,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first'
  });

  const valuationLabel = multiLineEnabled ? 'Multi-Line' : 'DCF';
  const currentValue = (() => {
    if (!shouldFetchVal) return 0;
    if (multiLineEnabled) {
      if (hasScenarioMid) return scenarioMid;
      const mid = Number(multiLineValData?.multiLineValuation?.combined_summary_adjusted?.mid || multiLineValData?.multiLineValuation?.combined_summary?.mid || 0);
      return Number.isFinite(mid) ? mid : 0;
    }
    const v = Number(legacyValData?.successionValuationData?.valueOfPractice || 0);
    return Number.isFinite(v) ? v : 0;
  })();

  const hasValueData = (() => {
    if (!shouldFetchVal) return false;
    if (multiLineEnabled) return hasScenarioMid || !!multiLineValData?.multiLineValuation;
    return !!legacyValData?.successionValuationData;
  })();
  const valueGap = Number(exitValueNeeded || 0) - Number(currentValue || 0);
  const valueHasSurplus = valueGap < 0;
  const valueAbsGap = Math.abs(valueGap);
  const pctToGoal = exitValueNeeded > 0 ? Math.min(100, Math.max(0, (currentValue / exitValueNeeded) * 100)) : 0;

  const DEFAULT_GROWTH = 0.06;
  const computeYearsToGoal = (current, goal, growth = DEFAULT_GROWTH, maxYears = 10) => {
    const numericCurrent = Number(current || 0);
    const numericGoal = Number(goal || 0);
    if (numericGoal <= 0) return 0;
    if (numericCurrent >= numericGoal) return 0;
    let year = 0;
    let value = numericCurrent;
    while (year < maxYears && value < numericGoal) {
      year += 1;
      value = value * (1 + growth);
      if (value >= numericGoal) return year;
    }
    return null;
  };

  const yearsToClose = computeYearsToGoal(currentValue, exitValueNeeded, DEFAULT_GROWTH, 10);
  const canCloseInTime = yearsToClose != null && yearsToClose <= (years.max || 10);

  // Persist value gap calculation to user fields (stamps value_gap TriggeringEvent server-side).
  // Only do this when we have a valuation + wealth goal, and avoid spamming repeated writes.
  useEffect(() => {
    if (!hasValueData) return;
    if (!hasWealthGapEvent) return;
    if (!lastAccess) return;

    // Only persist when multi-line valuation is enabled and has been run (per requirements).
    if (!multiLineEnabled) return;

    const roundedCurrent = Math.round(Number(currentValue || 0));
    const roundedGap = Math.round(Number(valueGap || 0));
    if (!Number.isFinite(roundedCurrent) || !Number.isFinite(roundedGap)) return;
    // Avoid persisting meaningless 0s; only persist when we have a usable valuation mid.
    if (roundedCurrent <= 0) return;

    const storedCurrent = Math.round(Number(u?.value_gap_current_value || 0));
    const storedGap = Math.round(Number(u?.value_gap_gap_value || 0));
    const isAlreadyUpToDate = storedCurrent === roundedCurrent && storedGap === roundedGap;
    if (isAlreadyUpToDate) return;

    const key = `${roundedCurrent}:${roundedGap}`;
    if (lastPersistKeyRef.current === key) return;

    // Retry-safe: don't permanently block retries on transient failures.
    // We cap retries per key to avoid infinite loops.
    const retryCount = Number(persistRetryCountRef.current[key] || 0);
    if (retryCount >= 3) return;

    // Clear any pending retry timer
    if (persistRetryTimeoutRef.current) {
      clearTimeout(persistRetryTimeoutRef.current);
      persistRetryTimeoutRef.current = null;
    }

    updateValueGap({
      variables: {
        value_gap_current_value: roundedCurrent,
        value_gap_gap_value: roundedGap,
        value_gap_last_updated_at: new Date().toISOString(),
      },
    })
      .then(() => {
        // Mark as persisted only on success
        lastPersistKeyRef.current = key;
        persistRetryCountRef.current[key] = 0;
      })
      .catch(() => {
        const nextCount = retryCount + 1;
        persistRetryCountRef.current[key] = nextCount;
        // Backoff retry (1.5s, 3s, 4.5s)
        persistRetryTimeoutRef.current = setTimeout(() => {
          setPersistRetryTick((t) => t + 1);
        }, 1500 * nextCount);
      });

    return () => {
      if (persistRetryTimeoutRef.current) {
        clearTimeout(persistRetryTimeoutRef.current);
        persistRetryTimeoutRef.current = null;
      }
    };
  }, [
    hasValueData,
    hasWealthGapEvent,
    lastAccess,
    multiLineEnabled,
    currentValue,
    valueGap,
    u?.value_gap_current_value,
    u?.value_gap_gap_value,
    updateValueGap,
    persistRetryTick
  ]);

  if (loading) {
    return (
      <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 ${className}`}>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-succession-bg-primary border border-succession-border rounded-lg p-3 animate-pulse">
            <div className="h-6 bg-succession-bg-secondary rounded w-1/2 mb-3"></div>
            <div className="h-8 bg-succession-bg-secondary rounded w-3/4"></div>
          </div>
        ))}
      </div>
    );
  }

  if (!hasAnyCompleted && !hasAnswers) {
    return null;
  }

  return (
    <>
      <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 ${className}`}>
        {/* Card 1: Personal Readiness Score */}
        <div className="bg-succession-bg-primary border border-succession-border rounded-lg p-3 hover:shadow-succession-lg transition-shadow">
          <div className="flex items-center justify-end mb-3">
            <button
              onClick={() => setPersonalModal(true)}
              className="text-succession-text-secondary hover:text-succession-primary transition-colors"
              aria-label={intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.learn_more_personal' })}
              disabled={!hasReadinessData}
            >
              <Info className="w-3.5 h-3.5" />
            </button>
          </div>
          <div>
            {hasReadinessData ? (
              <>
                <p className={`text-2xl font-bold leading-none ${getScoreColor(ownerReadinessScore)}`}>
                  {ownerReadinessScore}{pctSymbol()}
                </p>
                <p className="text-[10px] text-succession-text-secondary mt-1 uppercase tracking-wide">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.personal_readiness_label' })}</p>
                <p className="text-sm font-semibold mt-1">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.personal_readiness_subtitle' })}</p>
              </>
            ) : (
              <>
                <p className="text-2xl font-bold leading-none text-succession-text-secondary">—</p>
                <p className="text-[10px] text-succession-text-secondary mt-1 uppercase tracking-wide">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.personal_readiness_label' })}</p>
                <p className="text-sm font-semibold mt-1">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.no_data' })}</p>
              </>
            )}
          </div>
        </div>

        {/* Card 2: Buyer Attractiveness Score */}
        <div className="bg-succession-bg-primary border border-succession-border rounded-lg p-3 hover:shadow-succession-lg transition-shadow">
          <div className="flex items-center justify-end mb-3">
            <button
              onClick={() => setBuyerModal(true)}
              className="text-succession-text-secondary hover:text-succession-primary transition-colors"
              aria-label={intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.learn_more_buyer' })}
              disabled={!hasReadinessData}
            >
              <Info className="w-3.5 h-3.5" />
            </button>
          </div>
          <div>
            {hasReadinessData ? (
              <>
                <p className={`text-2xl font-bold leading-none ${getScoreColor(buyerAttractivenessScore)}`}>
                  {buyerAttractivenessScore}{pctSymbol()}
                </p>
                <p className="text-[10px] text-succession-text-secondary mt-1 uppercase tracking-wide">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.buyer_attractiveness_label' })}</p>
                <p className="text-sm font-semibold mt-1">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.buyer_attractiveness_subtitle' })}</p>
              </>
            ) : (
              <>
                <p className="text-2xl font-bold leading-none text-succession-text-secondary">—</p>
                <p className="text-[10px] text-succession-text-secondary mt-1 uppercase tracking-wide">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.buyer_attractiveness_label' })}</p>
                <p className="text-sm font-semibold mt-1">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.no_data' })}</p>
              </>
            )}
          </div>
        </div>

        {/* Card 3: Wealth Gap */}
        <div className="bg-succession-bg-primary border border-succession-border rounded-lg p-3 hover:shadow-succession-lg transition-shadow">
          <div className="flex items-center justify-end mb-3">
            <button
              onClick={() => setWealthModal(true)}
              className="text-succession-text-secondary hover:text-succession-primary transition-colors"
              aria-label={intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.learn_more_wealth' })}
              disabled={!hasWealthData}
            >
              <Info className="w-3.5 h-3.5" />
            </button>
          </div>
          <div>
            {hasWealthData ? (
              <>
                <p className={`text-2xl font-bold leading-none ${wealthHasSurplus ? 'text-succession-success' : 'text-succession-error'}`}>
                  {currency(wealthAbsGap)}
                </p>
                <p className="text-[10px] text-succession-text-secondary mt-1 uppercase tracking-wide">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.wealth_gap_label' })}</p>
                <p className="text-sm font-semibold mt-1">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.wealth_gap_subtitle' })}</p>
              </>
            ) : (
              <>
                <p className="text-2xl font-bold leading-none text-succession-text-secondary">—</p>
                <p className="text-[10px] text-succession-text-secondary mt-1 uppercase tracking-wide">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.wealth_gap_label' })}</p>
                <p className="text-sm font-semibold mt-1">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.no_data' })}</p>
              </>
            )}
          </div>
        </div>

        {/* Card 4: Value Gap */}
        <div className="bg-succession-bg-primary border border-succession-border rounded-lg p-3 hover:shadow-succession-lg transition-shadow">
          <div className="flex items-center justify-end mb-3">
            <button
              onClick={() => setValueModal(true)}
              className="text-succession-text-secondary hover:text-succession-primary transition-colors"
              aria-label={intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.learn_more_value' })}
              disabled={!hasValueData}
            >
              <Info className="w-3.5 h-3.5" />
            </button>
          </div>
          <div>
            {!lastAccess ? (
              <>
                <p className="text-2xl font-bold leading-none text-succession-text-secondary">—</p>
                <p className="text-[10px] text-succession-text-secondary mt-1 uppercase tracking-wide">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.value_gap_label' })}</p>
                <p className="text-sm font-semibold mt-1">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.value_gap_run_valuation' })}</p>
                <Button
                  as="a"
                  href="/succession/valuation"
                  size="sm"
                  className="mt-3 w-full"
                >
                  {intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.get_valuation_cta' })}
                </Button>
              </>
            ) : hasValueData ? (
              <>
                <p className={`text-2xl font-bold leading-none ${valueHasSurplus ? 'text-succession-success' : 'text-succession-error'}`}>
                  {currency(valueAbsGap)}
                </p>
                <p className="text-[10px] text-succession-text-secondary mt-1 uppercase tracking-wide">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.value_gap_label' })}</p>
                <p className="text-sm font-semibold mt-1">
                  {valueHasSurplus
                    ? intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.value_gap_meets_goal' })
                    : intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.value_gap_needs_growth' })}
                </p>
              </>
            ) : (
              <>
                <p className="text-2xl font-bold leading-none text-succession-text-secondary">—</p>
                <p className="text-[10px] text-succession-text-secondary mt-1 uppercase tracking-wide">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.value_gap_label' })}</p>
                <p className="text-sm font-semibold mt-1">{intl.formatMessage({ id: 'succession.value_acceleration.your_gaps.no_data' })}</p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {hasReadinessData && (
        <>
          <ReadinessScoreModal
            isOpen={personalModal}
            onClose={() => setPersonalModal(false)}
            type="personal"
            score={ownerReadinessScore}
          />
          <ReadinessScoreModal
            isOpen={buyerModal}
            onClose={() => setBuyerModal(false)}
            type="buyer"
            score={buyerAttractivenessScore}
          />
        </>
      )}

      {hasWealthData && (
        <WealthGapModal
          isOpen={wealthModal}
          onClose={() => setWealthModal(false)}
          data={{ current: wealthCurrent, goal: wealthGoal, gap: wealthGap, hasSurplus: wealthHasSurplus, absGap: wealthAbsGap }}
          years={years}
        />
      )}

      {hasValueData && (
        <ValueGapModal
          isOpen={valueModal}
          onClose={() => setValueModal(false)}
          data={{ exitValueNeeded, currentValue, hasSurplus: valueHasSurplus, absGap: valueAbsGap, pctToGoal, yearsToClose, canCloseInTime, valuationLabel }}
          years={years}
        />
      )}
    </>
  );
};

export default YourGapsCompact;
